import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, signal, Component, inject, input, output } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { AuthService } from '@/core/auth/auth.service';
import { VistoTemporarioSolicitacaoService } from '@/core/api/sicom-api/registro/medico/visto-temporario/visto-temporario-solicitacao.service';
import type {
    VistoTemporarioSolicitacaoAssuntoFinalidadeResponse,
    VistoTemporarioSolicitacaoEnderecoResponse,
    VistoTemporarioSolicitacaoJaSolicitadoResponse,
    VistoTemporarioSolicitacaoMedicoResponse,
    VistoTemporarioSolicitacaoPeriodoResponse,
    VistoTemporarioSolicitacaoPesquisaRequest,
    VistoTemporarioSolicitacaoPesquisaResponse,
    VistoTemporarioSolicitacaoSolicitanteResponse
} from '@/core/api/sicom-api/registro/medico/visto-temporario/visto-temporario-solicitacao.model';
import type { VistoTemporarioProtocoloSelecionado } from '../visto-temporario.types';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { DateUtils } from '@/shared/utils/date-utils';
import { Button } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { Textarea } from 'primeng/textarea';
import { finalize, forkJoin, of, switchMap } from 'rxjs';
import { CardContent } from '@/shared/components/card-content/card-content';
import { PessoaAvatarComponent } from '@/shared/components/pessoa/pessoa-avatar/pessoa-avatar';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
interface ProtocoloRow {
    idProtocolo?: number;
    numeroProtocolo: string;
    anoProtocolo: string;
    idAssunto?: number;
}

interface SolicitanteRow {
    uf: string;
    solicitante: string;
    cpfCnpj?: string;
    /** Id interno do solicitante (não há CRM nesta consulta) — identidade usada pelo `app-pessoa-avatar`. */
    idSolicitante: number | null;
}

interface AssuntoRow {
    assunto: string;
}

@Component({
    selector: 'app-localizar-visto',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [DialogoPadraoDirective, CommonModule, ReactiveFormsModule, Select, InputText, Button, TableModule, Textarea, CardContent, PessoaAvatarComponent, DialogModule],
    templateUrl: './localizar-visto.html',
    styleUrl: './localizar-visto.scss'
})
/** Localização de solicitações de visto temporário. */
export class LocalizarVistoComponent {
    pessoa = input<Pessoa>();
    confirmar = output<VistoTemporarioProtocoloSelecionado>();
    cancelar = output<void>();

    private readonly fb = inject(FormBuilder);
    private readonly authService = inject(AuthService);
    private readonly vistoTemporarioSolicitacaoService = inject(VistoTemporarioSolicitacaoService);

    filtroForm = this.fb.group({
        tipo: ['90_CORRIDOS'],
        solicitacaoVisto: ['LOCAL'],
        numeroProtocolo: [''],
        ano: [''],
        solicitanteVistoTemporario: ['']
    });

    protocoloForm = this.fb.group({
        finalidade: ['']
    });

    tipos = [
        { label: '90 Corridos', value: '90_CORRIDOS' },
        { label: '90 Fracionados', value: '90_FRACIONADOS' }
    ];
    solicitacoesVisto = [
        { label: 'Local', value: 'LOCAL' },
        { label: 'Outro CRM', value: 'OUTRO_CRM' }
    ];

    protocolos: ProtocoloRow[] = [];
    solicitantes: SolicitanteRow[] = [];
    assuntos: AssuntoRow[] = [];
    pesquisou = false;
    pesquisando = signal(false);
    carregandoSolicitantes = signal(false);
    /** Foto ampliada do solicitante clicado — mesmo padrão de zoom da Capa de PAT/Dívida Ativa. */
    fotoAmpliada = signal<string | null>(null);
    fotoAmpliadaVisivel = signal(false);
    erroPesquisa = '';
    erroSelecaoProtocolo = '';
    protocoloSelecionadoId?: number;
    protocoloSelecionado: ProtocoloRow | null = null;
    detalhesAssuntoFinalidadeSelecionados: VistoTemporarioSolicitacaoAssuntoFinalidadeResponse[] = [];
    detalhesPeriodosSelecionados: VistoTemporarioSolicitacaoPeriodoResponse[] = [];
    detalhesEnderecoContatoSelecionados: VistoTemporarioSolicitacaoEnderecoResponse[] = [];
    detalhesEnderecoTrabalhoSelecionados: VistoTemporarioSolicitacaoEnderecoResponse[] = [];
    detalhesMedicosSelecionados: VistoTemporarioSolicitacaoMedicoResponse[] = [];
    detalhesSolicitanteInteressadoSelecionados: VistoTemporarioSolicitacaoSolicitanteResponse[] = [];
    detalhesVistosJaSolicitadosSelecionados: VistoTemporarioSolicitacaoJaSolicitadoResponse[] = [];

    onPesquisar(): void {
        this.erroPesquisa = '';
        this.erroSelecaoProtocolo = '';

        const payload = this.mapearPayloadPesquisa();
        if (!payload) {
            this.pesquisou = true;
            this.protocolos = [];
            this.assuntos = [];
            this.solicitantes = [];
            this.protocoloSelecionadoId = undefined;
            this.protocoloSelecionado = null;
            this.detalhesAssuntoFinalidadeSelecionados = [];
            this.detalhesPeriodosSelecionados = [];
            this.detalhesEnderecoContatoSelecionados = [];
            this.detalhesEnderecoTrabalhoSelecionados = [];
            this.detalhesMedicosSelecionados = [];
            this.detalhesSolicitanteInteressadoSelecionados = [];
            this.detalhesVistosJaSolicitadosSelecionados = [];
            this.protocoloForm.patchValue({ finalidade: '' });
            this.erroPesquisa = 'Não foi possível identificar a UF do usuário logado.';
            return;
        }

        this.pesquisando.set(true);

        this.vistoTemporarioSolicitacaoService
            .pesquisarSolicitacoes(payload)
            .pipe(
                finalize(() => {
                    this.pesquisando.set(false);
                })
            )
            .subscribe({
                next: (resposta) => {
                    this.pesquisou = true;
                    this.protocolos = resposta.map((item) => this.mapearProtocolo(item));
                    this.assuntos = [];
                    this.solicitantes = [];
                    this.protocoloSelecionadoId = undefined;
                    this.protocoloSelecionado = null;
                    this.detalhesAssuntoFinalidadeSelecionados = [];
                    this.detalhesPeriodosSelecionados = [];
                    this.detalhesEnderecoContatoSelecionados = [];
                    this.detalhesEnderecoTrabalhoSelecionados = [];
                    this.detalhesMedicosSelecionados = [];
                    this.detalhesSolicitanteInteressadoSelecionados = [];
                    this.detalhesVistosJaSolicitadosSelecionados = [];
                    this.protocoloForm.patchValue({ finalidade: '' });

                    if (this.protocolos.length > 0) {
                        this.onSelecionarProtocolo(this.protocolos[0]);
                    }
                },
                error: (erro) => {
                    this.pesquisou = true;
                    this.protocolos = [];
                    this.assuntos = [];
                    this.solicitantes = [];
                    this.protocoloSelecionadoId = undefined;
                    this.protocoloSelecionado = null;
                    this.detalhesAssuntoFinalidadeSelecionados = [];
                    this.detalhesPeriodosSelecionados = [];
                    this.detalhesEnderecoContatoSelecionados = [];
                    this.detalhesEnderecoTrabalhoSelecionados = [];
                    this.detalhesMedicosSelecionados = [];
                    this.detalhesSolicitanteInteressadoSelecionados = [];
                    this.detalhesVistosJaSolicitadosSelecionados = [];
                    this.erroPesquisa = this.extrairMensagemErro(erro) ?? 'Não foi possível pesquisar as solicitações de visto.';
                }
            });
    }

    onSelecionarProtocolo(protocolo: ProtocoloRow): void {
        if (protocolo.idProtocolo == null) {
            this.erroSelecaoProtocolo = 'Não foi possível selecionar este protocolo.';
            this.solicitantes = [];
            this.assuntos = [];
            this.protocoloSelecionado = null;
            this.detalhesAssuntoFinalidadeSelecionados = [];
            this.detalhesPeriodosSelecionados = [];
            this.detalhesEnderecoContatoSelecionados = [];
            this.detalhesEnderecoTrabalhoSelecionados = [];
            this.detalhesMedicosSelecionados = [];
            this.detalhesSolicitanteInteressadoSelecionados = [];
            this.detalhesVistosJaSolicitadosSelecionados = [];
            this.protocoloForm.patchValue({ finalidade: '' });
            return;
        }

        const idProtocolo = protocolo.idProtocolo;
        this.protocoloSelecionadoId = idProtocolo;
        this.protocoloSelecionado = protocolo;
        this.erroSelecaoProtocolo = '';
        this.carregandoSolicitantes.set(true);
        this.solicitantes = [];
        this.assuntos = [];
        this.detalhesAssuntoFinalidadeSelecionados = [];
        this.detalhesPeriodosSelecionados = [];
        this.detalhesEnderecoContatoSelecionados = [];
        this.detalhesEnderecoTrabalhoSelecionados = [];
        this.detalhesMedicosSelecionados = [];
        this.detalhesSolicitanteInteressadoSelecionados = [];
        this.detalhesVistosJaSolicitadosSelecionados = [];
        this.protocoloForm.patchValue({ finalidade: '' });

        this.vistoTemporarioSolicitacaoService
            .buscarSolicitantesPorProtocolo(idProtocolo)
            .pipe(
                switchMap((solicitantes) => {
                    const solicitantePrincipal = solicitantes.find((item) => item.idSolicitante != null && (item.ufSgUf ?? '').trim().length > 0);
                    const vistosJaSolicitados$ = solicitantePrincipal
                        ? this.vistoTemporarioSolicitacaoService.buscarVistosJaSolicitadosPorMedico(solicitantePrincipal.idSolicitante, solicitantePrincipal.ufSgUf.trim(), idProtocolo)
                        : of<VistoTemporarioSolicitacaoJaSolicitadoResponse[]>([]);

                    return forkJoin({
                        solicitantes: of(solicitantes),
                        assuntoFinalidade: this.vistoTemporarioSolicitacaoService.buscarAssuntoFinalidadePorProtocolo(idProtocolo),
                        periodos: this.vistoTemporarioSolicitacaoService.buscarPeriodosPorProtocolo(idProtocolo),
                        enderecoContato: this.vistoTemporarioSolicitacaoService.buscarEnderecosPorProtocoloETipo(idProtocolo, 'C'),
                        enderecoTrabalho: this.vistoTemporarioSolicitacaoService.buscarEnderecosPorProtocoloETipo(idProtocolo, 'T'),
                        medicos: this.vistoTemporarioSolicitacaoService.buscarMedicosPorProtocolo(idProtocolo),
                        solicitanteInteressado: this.vistoTemporarioSolicitacaoService.buscarSolicitanteInteressadoPorProtocolo(idProtocolo),
                        vistosJaSolicitados: vistosJaSolicitados$
                    });
                }),
                finalize(() => {
                    if (this.protocoloSelecionadoId === idProtocolo) {
                        this.carregandoSolicitantes.set(false);
                    }
                })
            )
            .subscribe({
                next: (resposta) => {
                    if (this.protocoloSelecionadoId !== idProtocolo) {
                        return;
                    }

                    this.solicitantes = resposta.solicitantes.map((item) => this.mapearSolicitante(item));
                    this.detalhesAssuntoFinalidadeSelecionados = resposta.assuntoFinalidade;
                    this.detalhesPeriodosSelecionados = resposta.periodos;
                    this.detalhesEnderecoContatoSelecionados = resposta.enderecoContato;
                    this.detalhesEnderecoTrabalhoSelecionados = resposta.enderecoTrabalho;
                    this.detalhesMedicosSelecionados = resposta.medicos;
                    this.detalhesSolicitanteInteressadoSelecionados = resposta.solicitanteInteressado;
                    this.detalhesVistosJaSolicitadosSelecionados = resposta.vistosJaSolicitados;
                    this.assuntos = this.mapearAssuntos(resposta.assuntoFinalidade);
                    this.protocoloForm.patchValue({ finalidade: this.extrairFinalidade(resposta.assuntoFinalidade) });
                },
                error: (erro) => {
                    if (this.protocoloSelecionadoId !== idProtocolo) {
                        return;
                    }

                    this.solicitantes = [];
                    this.assuntos = [];
                    this.detalhesAssuntoFinalidadeSelecionados = [];
                    this.detalhesPeriodosSelecionados = [];
                    this.detalhesEnderecoContatoSelecionados = [];
                    this.detalhesEnderecoTrabalhoSelecionados = [];
                    this.detalhesMedicosSelecionados = [];
                    this.detalhesSolicitanteInteressadoSelecionados = [];
                    this.detalhesVistosJaSolicitadosSelecionados = [];
                    this.protocoloForm.patchValue({ finalidade: '' });
                    this.erroSelecaoProtocolo = this.extrairMensagemErro(erro) ?? 'Não foi possível carregar os dados do protocolo selecionado.';
                }
            });
    }

    isProtocoloSelecionado(protocolo: ProtocoloRow): boolean {
        return protocolo.idProtocolo != null && protocolo.idProtocolo === this.protocoloSelecionadoId;
    }

    ampliarFoto(foto: string): void {
        this.fotoAmpliada.set(foto);
        this.fotoAmpliadaVisivel.set(true);
    }

    onConfirmar(): void {
        if (this.carregandoSolicitantes()) {
            this.erroSelecaoProtocolo = 'Aguarde o carregamento dos dados do protocolo selecionado.';
            return;
        }

        const dadosSelecionados = this.montarProtocoloSelecionadoParaConfirmacao();
        if (!dadosSelecionados) {
            this.erroSelecaoProtocolo = 'Selecione um protocolo para confirmar.';
            return;
        }

        this.confirmar.emit(dadosSelecionados);
    }

    onCancelar(): void {
        this.cancelar.emit();
    }

    private mapearProtocolo(item: VistoTemporarioSolicitacaoPesquisaResponse): ProtocoloRow {
        return {
            idProtocolo: item.idProtocolo,
            numeroProtocolo: item.numeroProtocolo,
            anoProtocolo: item.anoProtocolo,
            idAssunto: item.idAssunto
        };
    }

    private mapearSolicitante(item: VistoTemporarioSolicitacaoSolicitanteResponse): SolicitanteRow {
        return {
            uf: item.ufSgUf,
            solicitante: item.nomeSolicitante,
            cpfCnpj: item.nuCpfCgc,
            idSolicitante: typeof item.idSolicitante === 'number' && Number.isFinite(item.idSolicitante) ? item.idSolicitante : null
        };
    }

    private mapearPayloadPesquisa(): VistoTemporarioSolicitacaoPesquisaRequest | null {
        const form = this.filtroForm.getRawValue();
        const sgUfProtocolo = this.authService.ufDoUsuario()?.trim().toUpperCase() ?? '';
        if (!sgUfProtocolo) {
            return null;
        }

        return {
            sgUfProtocolo,
            tipoVisto: form.tipo === '90_FRACIONADOS' ? 1 : 0,
            localVisto: form.solicitacaoVisto === 'OUTRO_CRM' ? 1 : 0,
            numeroProtocolo: this.parseNumeroPositivo(form.numeroProtocolo),
            anoProtocolo: this.parseNumeroPositivo(form.ano),
            nomeSolicitante: this.parseTexto(form.solicitanteVistoTemporario)
        };
    }

    private mapearAssuntos(assuntoFinalidade: VistoTemporarioSolicitacaoAssuntoFinalidadeResponse[]): AssuntoRow[] {
        const assuntos = assuntoFinalidade.map((item) => this.extrairAssunto(item)).filter((assunto) => assunto.length > 0);
        const assuntosUnicos = [...new Set(assuntos)];
        return assuntosUnicos.map((assunto) => ({ assunto }));
    }

    private extrairFinalidade(assuntoFinalidade: VistoTemporarioSolicitacaoAssuntoFinalidadeResponse[]): string {
        for (const item of assuntoFinalidade) {
            const finalidade = (item.finalidade ?? '').trim();
            if (finalidade) {
                return finalidade;
            }
        }

        return '';
    }

    private montarProtocoloSelecionadoParaConfirmacao(): VistoTemporarioProtocoloSelecionado | null {
        if (this.protocoloSelecionado?.idProtocolo == null) {
            return null;
        }

        const tipoVisto = this.extrairPrimeiroTexto(this.detalhesAssuntoFinalidadeSelecionados.map((item) => this.extrairAssunto(item)));
        const finalidade = this.extrairFinalidade(this.detalhesAssuntoFinalidadeSelecionados) || undefined;
        const numeroDocumentoOrigem = this.extrairPrimeiroTexto(this.detalhesAssuntoFinalidadeSelecionados.map((item) => item.numeroDocumentoOrigem));
        const inOrigem = this.extrairPrimeiroTexto(this.detalhesAssuntoFinalidadeSelecionados.map((item) => item.inOrigem));
        const solicitante = this.extrairPrimeiroTexto(this.solicitantes.map((item) => item.solicitante));
        const solicitanteCpfCnpj = this.extrairPrimeiroTexto(this.solicitantes.map((item) => item.cpfCnpj));
        const periodoInicio = this.extrairPrimeiroTexto(this.detalhesPeriodosSelecionados.map((item) => this.formatarDataPeriodo(item.dataInicioPeriodo)));
        const periodoFim = this.extrairPrimeiroTexto(this.detalhesPeriodosSelecionados.map((item) => this.formatarDataPeriodo(item.dataFinalPeriodo)));
        const inSituacao = this.extrairPrimeiroTexto(this.detalhesPeriodosSelecionados.map((item) => item.inSituacao));
        const idSolicitante = this.extrairPrimeiroNumero(this.detalhesPeriodosSelecionados.map((item) => item.idSolicitante));
        const ufSgUf = this.extrairPrimeiroTexto(this.detalhesPeriodosSelecionados.map((item) => item.ufSgUf));
        const ufOrigem = this.extrairPrimeiroTexto(this.detalhesPeriodosSelecionados.map((item) => item.sgUfOrigem));
        const crmOrigem = this.extrairPrimeiroTexto(this.detalhesPeriodosSelecionados.map((item) => item.nuCrmOrigem));
        const requerente = this.extrairPrimeiroTexto(this.detalhesSolicitanteInteressadoSelecionados.map((item) => item.nomeSolicitante));
        const requerenteCpfCnpj = this.extrairPrimeiroTexto(this.detalhesSolicitanteInteressadoSelecionados.map((item) => item.nuCpfCgc));

        return {
            idProtocolo: this.protocoloSelecionado.idProtocolo,
            idSolicitante,
            ufSgUf,
            numeroProtocolo: this.protocoloSelecionado.numeroProtocolo,
            anoProtocolo: this.protocoloSelecionado.anoProtocolo,
            tipoVisto,
            solicitante,
            solicitanteCpfCnpj,
            requerente,
            requerenteCpfCnpj,
            finalidade,
            numeroDocumentoOrigem,
            inOrigem,
            periodoInicio,
            periodoFim,
            inSituacao,
            ufOrigem,
            crmOrigem,
            contatoCep: this.extrairPrimeiroCampoEndereco(this.detalhesEnderecoContatoSelecionados, (item) => item.nuCep),
            contatoEndereco: this.extrairPrimeiroCampoEndereco(this.detalhesEnderecoContatoSelecionados, (item) => item.txEndereco),
            contatoBairro: this.extrairPrimeiroCampoEndereco(this.detalhesEnderecoContatoSelecionados, (item) => item.nmBairro),
            contatoMunicipio: this.extrairPrimeiroCampoEndereco(this.detalhesEnderecoContatoSelecionados, (item) => item.dsMunicipio),
            contatoIdMunicipio: this.extrairPrimeiroNumero(this.detalhesEnderecoContatoSelecionados.map((item) => item.idMunicipio)),
            contatoDdd: this.extrairPrimeiroCampoEndereco(this.detalhesEnderecoContatoSelecionados, (item) => item.nuCodigoDdd),
            contatoTelefone: this.extrairPrimeiroCampoEndereco(this.detalhesEnderecoContatoSelecionados, (item) => item.nuTelefone),
            trabalhoCep: this.extrairPrimeiroCampoEndereco(this.detalhesEnderecoTrabalhoSelecionados, (item) => item.nuCep),
            trabalhoEndereco: this.extrairPrimeiroCampoEndereco(this.detalhesEnderecoTrabalhoSelecionados, (item) => item.txEndereco),
            trabalhoBairro: this.extrairPrimeiroCampoEndereco(this.detalhesEnderecoTrabalhoSelecionados, (item) => item.nmBairro),
            trabalhoMunicipio: this.extrairPrimeiroCampoEndereco(this.detalhesEnderecoTrabalhoSelecionados, (item) => item.dsMunicipio),
            trabalhoIdMunicipio: this.extrairPrimeiroNumero(this.detalhesEnderecoTrabalhoSelecionados.map((item) => item.idMunicipio)),
            trabalhoDdd: this.extrairPrimeiroCampoEndereco(this.detalhesEnderecoTrabalhoSelecionados, (item) => item.nuCodigoDdd),
            trabalhoTelefone: this.extrairPrimeiroCampoEndereco(this.detalhesEnderecoTrabalhoSelecionados, (item) => item.nuTelefone),
            medicosRelacionados: this.detalhesMedicosSelecionados.map((item) => ({
                uf: (item.sgUf ?? '').trim(),
                medico: (item.nomeSolicitante ?? '').trim(),
                cpf: (item.nuCpfCgc ?? '').trim(),
                crm: (item.nuCrm ?? '').trim() || undefined
            })),
            outrosVistosCadastrados: this.detalhesVistosJaSolicitadosSelecionados.map((item) => ({
                apto: (item.inSituacao ?? '').trim(),
                dataInicio: this.formatarDataPeriodo(item.dataInicioPeriodo) ?? '',
                dataFim: this.formatarDataPeriodo(item.dataFinalPeriodo) ?? '',
                dias: this.formatarTotalDias(item.total)
            }))
        };
    }

    private extrairAssunto(item: VistoTemporarioSolicitacaoAssuntoFinalidadeResponse): string {
        return (item.ds_assunto ?? item.dsAssunto ?? item.assunto ?? '').trim();
    }

    private extrairPrimeiroTexto(valores: Array<string | null | undefined>): string | undefined {
        for (const valor of valores) {
            const texto = (valor ?? '').trim();
            if (texto) {
                return texto;
            }
        }

        return undefined;
    }

    private extrairPrimeiroNumero(valores: Array<number | null | undefined>): number | undefined {
        for (const valor of valores) {
            if (typeof valor === 'number' && Number.isFinite(valor)) {
                return valor;
            }
        }

        return undefined;
    }

    private extrairPrimeiroCampoEndereco(enderecos: VistoTemporarioSolicitacaoEnderecoResponse[], seletor: (endereco: VistoTemporarioSolicitacaoEnderecoResponse) => string | null | undefined): string | undefined {
        for (const endereco of enderecos) {
            const valor = this.extrairPrimeiroTexto([seletor(endereco)]);
            if (valor) {
                return valor;
            }
        }

        return undefined;
    }

    private formatarDataPeriodo(valor: string | null | undefined): string | undefined {
        const data = (valor ?? '').trim();
        if (!data) {
            return undefined;
        }

        if (DateUtils.isDateString(data)) {
            return DateUtils.formatDate(data);
        }

        return data;
    }

    private formatarTotalDias(total: number | null | undefined): string {
        if (typeof total !== 'number' || !Number.isFinite(total)) {
            return '';
        }

        return Number.isInteger(total) ? total.toString() : total.toLocaleString('pt-BR');
    }

    private parseNumeroPositivo(valor: unknown): number | undefined {
        if (valor == null) {
            return undefined;
        }

        const somenteDigitos = valor.toString().trim().replace(/\D+/g, '');
        if (!somenteDigitos) {
            return undefined;
        }

        const numero = Number(somenteDigitos);
        return Number.isFinite(numero) && numero > 0 ? numero : undefined;
    }

    private parseTexto(valor: unknown): string | undefined {
        const texto = (valor ?? '').toString().trim();
        return texto || undefined;
    }

    private extrairMensagemErro(erro: unknown): string | undefined {
        const e = erro as { error?: { message?: string; detail?: string; error?: string }; message?: string; statusText?: string };
        return e?.error?.message || e?.error?.detail || e?.error?.error || e?.message || e?.statusText;
    }
}
